import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { MailCheck, MailWarning, MailX, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { SiteShell } from "@/components/layout/SiteShell";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

/**
 * VerifyEmailPage — handles the `/verify-email?token=…` landing.
 *
 * Renders four terminal states based on the backend response:
 *   - success       — token accepted, refresh the AuthContext user.
 *   - already       — user was already verified (e.g. clicked link twice).
 *   - expired       — token past its 24h window, offer Resend.
 *   - invalid       — malformed / tampered / wrong purpose.
 *   - loading       — while the POST is in flight (initial state).
 */
export default function VerifyEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState("loading"); // loading|success|already|expired|invalid|error
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);

  const token = new URLSearchParams(location.search).get("token") || "";

  const verify = useCallback(async () => {
    if (!token) {
      setStatus("invalid");
      setMessage("This verification link is missing its token. Please open the link from your email again.");
      return;
    }
    try {
      const { data } = await api.post("/auth/verify-email", { token });
      if (data.already_verified) {
        setStatus("already");
      } else {
        setStatus("success");
      }
      // Refresh the AuthContext so the banner disappears without a reload.
      try {
        if (typeof refreshUser === "function") await refreshUser();
      } catch {}
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.code : null;
      const msg = detail && typeof detail === "object" ? detail.message : (typeof detail === "string" ? detail : null);
      if (code === "expired") setStatus("expired");
      else if (code === "invalid" || code === "user_not_found" || code === "email_mismatch") setStatus("invalid");
      else setStatus("error");
      setMessage(msg || "We couldn't verify your email. Please try again.");
    }
  }, [token, refreshUser]);

  useEffect(() => {
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    if (!user) {
      // Not signed in — send them to login. After login the dashboard
      // banner surfaces the Resend button.
      navigate("/login?next=/dashboard");
      return;
    }
    setResending(true);
    try {
      await api.post("/auth/verify-email/resend", {});
      toast.success("Verification email sent — check your inbox in a moment.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail && typeof detail === "object" ? detail.message : (typeof detail === "string" ? detail : "Could not resend right now.");
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  const Icon = (
    {
      loading: Loader2,
      success: MailCheck,
      already: MailCheck,
      expired: MailWarning,
      invalid: MailX,
      error: MailX,
    }[status] || MailX
  );

  const heading =
    {
      loading: "Verifying your email…",
      success: "Email verified",
      already: "Already verified",
      expired: "This link has expired",
      invalid: "Verification link isn't valid",
      error: "Something went wrong",
    }[status] || "Verification";

  return (
    <SiteShell>
      <section className="mx-auto max-w-lg px-6 py-16" data-testid="verify-email-page">
        <div className="card-soft text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--sb-cream-2))]">
            <Icon size={22} className={status === "loading" ? "animate-spin" : ""} />
          </div>
          <h1
            className="mt-5 font-serif text-3xl"
            data-testid={`verify-email-heading-${status}`}
          >
            {heading}
          </h1>
          {status === "loading" && (
            <p className="mt-3 text-sm text-muted-foreground">Just a moment while we confirm your email address.</p>
          )}
          {(status === "success" || status === "already") && (
            <>
              <p className="mt-3 text-sm text-muted-foreground">
                {status === "success"
                  ? "You can now send messages, apply to opportunities, and start Coffee Chats."
                  : "Your email was already verified — you're all set."}
              </p>
              <Link
                to="/dashboard"
                className="btn-primary mt-6 inline-flex px-6"
                data-testid="verify-email-continue-btn"
              >
                Go to dashboard
              </Link>
            </>
          )}
          {(status === "expired" || status === "invalid" || status === "error") && (
            <>
              <p className="mt-3 text-sm text-muted-foreground" data-testid="verify-email-error-message">
                {message}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="btn-primary px-6"
                  data-testid="verify-email-resend-btn"
                >
                  {resending ? "Sending…" : user ? "Resend verification email" : "Sign in to resend"}
                </button>
                <Link to="/dashboard" className="btn-secondary px-6 inline-flex items-center justify-center">
                  Back to dashboard
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </SiteShell>
  );
}
